import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import moment from "moment";
// @ts-ignore
import { saveAs } from "file-saver";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import { subscribeDashboardCustomers } from "../../components/firebase/customers";
import { createWeeklyOrderLabelsPdf, getBoxMultiplier, WeeklyLabelItem } from "./DashBoardWeeklyOrders";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./weeklyPlanning.css";

interface Customer {
  firebaseId: string;
  id: string;
  name: string;
  active: boolean;
}

interface FreshParent {
  id: string;
  name_sv: string;
  name_en: string;
  icon?: string;
  url?: string;
  link?: string;
  subtitle_sv?: string;
  mustard?: boolean;
}

interface FreshPackage {
  firebaseId: string;
  id: string;
  parentId: string;
  size: number;
  formatedSize: string;
  isBox?: boolean;
  active?: boolean;
  boundBagPackageId?: string;
  ean?: string;
}

interface PlannedWeek {
  firebaseId: string;
  weekKey: string;
  weekStart: number;
  createdAt: number;
}

interface PlannedOrder {
  firebaseId: string;
  weekKey: string;
  weekStart: number;
  customerId: string;
  customerName: string;
  productId: string;
  productName: string;
  packageId: string;
  packageLabel: string;
  quantity: number;
  createdAt: number;
  updatedAt?: number;
}

const PLANNED_WEEK_COLLECTION = "products_fresh_weekly_plan_week";
const PLANNED_ORDER_COLLECTION = "products_fresh_weekly_plan";
const currentWeekKey = () => moment().format("GGGG-[W]WW");
const plannedOrderId = (weekKey: string, customerId: string, packageId: string) =>
  [weekKey, customerId, packageId].map((value) => encodeURIComponent(value)).join("__");
const packageLabel = (packageItem: FreshPackage): string => packageItem.formatedSize || `${packageItem.size}g`;

interface PlannedPdfColumn {
  packageId: string;
  productName: string;
  packageName: string;
}

interface PlannedPdfProps {
  weekLabel: string;
  columns: PlannedPdfColumn[];
  customers: Customer[];
  orders: PlannedOrder[];
  exceptions: Record<string, number>;
}

const plannedPdfStyles = StyleSheet.create({
  page: {
    padding: 22,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    fontSize: 7,
  },
  title: {
    marginBottom: 3,
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
  },
  subtitle: {
    marginBottom: 10,
    color: "#555555",
    fontSize: 7,
  },
  table: {
    width: "100%",
    borderTop: "1px solid #111111",
    borderLeft: "1px solid #111111",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    minHeight: 27,
  },
  headerRow: {
    minHeight: 42,
  },
  cell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 3,
    borderRight: "1px solid #111111",
    borderBottom: "1px solid #111111",
    textAlign: "center",
  },
  customerCell: {
    width: 122,
    flexShrink: 0,
    alignItems: "flex-start",
    textAlign: "left",
  },
  packageCell: {
    // Use the same share of remaining space in every row, regardless of text length.
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  totalCell: {
    width: 42,
    flexShrink: 0,
  },
  headerCell: {
    backgroundColor: "#edf5f0",
    fontFamily: "Helvetica-Bold",
  },
  totalRow: {
    minHeight: 30,
    fontFamily: "Helvetica-Bold",
  },
  note: {
    marginTop: 8,
    color: "#555555",
    fontSize: 6.5,
  },
});

const PlannedWeekPdf = ({ weekLabel, columns, customers, orders, exceptions }: PlannedPdfProps) => {
  const getOrderQuantity = (customerId: string, packageId: string): number => orders
    .filter((order) => order.customerId === customerId && order.packageId === packageId)
    .reduce((sum, order) => sum + (order.quantity || 0), 0);

  const getCustomerTotal = (customerId: string): number => columns
    .reduce((sum, column) => sum + getOrderQuantity(customerId, column.packageId), 0);

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={plannedPdfStyles.page}>
        <Text style={plannedPdfStyles.title}>{weekLabel}</Text>
        <Text style={plannedPdfStyles.subtitle}>Planerad veckobeställning · Aktiva kunder</Text>
        <View style={plannedPdfStyles.table}>
          <View style={{ ...plannedPdfStyles.row, ...plannedPdfStyles.headerRow }} wrap={false}>
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.customerCell, ...plannedPdfStyles.headerCell }}>Kund</Text>
            {columns.map((column) => (
              <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.packageCell, ...plannedPdfStyles.headerCell }} key={column.packageId}>
                {column.productName}{"\n"}{column.packageName}
              </Text>
            ))}
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.totalCell, ...plannedPdfStyles.headerCell }}>Summa</Text>
          </View>
          {customers.map((customer) => (
            <View style={plannedPdfStyles.row} wrap={false} key={customer.firebaseId}>
              <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.customerCell }}>{customer.name}</Text>
              {columns.map((column) => {
                const amount = getOrderQuantity(customer.id, column.packageId);
                return <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.packageCell }} key={column.packageId}>{amount || ""}</Text>;
              })}
              <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.totalCell }}>{getCustomerTotal(customer.id) || ""}</Text>
            </View>
          ))}
          <View style={{ ...plannedPdfStyles.row, ...plannedPdfStyles.totalRow }} wrap={false}>
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.customerCell }}>Avvikelse</Text>
            {columns.map((column) => {
              const amount = exceptions[column.packageId] ?? 0;
              return <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.packageCell }} key={column.packageId}>{amount || ""}</Text>;
            })}
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.totalCell }}>{Object.values(exceptions).reduce((sum, value) => sum + (value || 0), 0) || ""}</Text>
          </View>
          <View style={{ ...plannedPdfStyles.row, ...plannedPdfStyles.totalRow }} wrap={false}>
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.customerCell }}>Summa</Text>
            {columns.map((column) => {
              const amount = customers.reduce((sum, customer) => sum + getOrderQuantity(customer.id, column.packageId), 0) + (exceptions[column.packageId] || 0);
              return <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.packageCell }} key={column.packageId}>{amount || ""}</Text>;
            })}
            <Text style={{ ...plannedPdfStyles.cell, ...plannedPdfStyles.totalCell }}>
              {customers.reduce((sum, customer) => sum + getCustomerTotal(customer.id), 0) + Object.values(exceptions).reduce((sum, value) => sum + (value || 0), 0) || ""}
            </Text>
          </View>
        </View>
        <Text style={plannedPdfStyles.note}>VERT.farm · Planerad veckobeställning</Text>
      </Page>
    </Document>
  );
};

const weekLabel = (week: PlannedWeek) => `Vecka ${moment(week.weekStart).isoWeek()} · ${moment(week.weekStart).isoWeekYear()}`;
const weekRange = (week: PlannedWeek) => `${moment(week.weekStart).format("D/M")}–${moment(week.weekStart).endOf("isoWeek").format("D/M YYYY")}`;

function PlanningDialog({ id, open, title, description, onClose, busy, error, wide, children, actions }: {
  id: string; open: boolean; title: string; description: string; onClose: () => void; busy?: boolean;
  error?: string; wide?: boolean; children: React.ReactNode; actions: React.ReactNode;
}) {
  return <Dialog className="FreshDialog BatchDialog PlanningDialog" fullWidth maxWidth={wide ? "md" : "sm"} open={open} onClose={busy ? undefined : onClose} aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
    <DialogTitle component="div" id={`${id}-heading`}><div><h2 id={`${id}-title`}>{title}</h2><p className="BatchDialogSubtitle" id={`${id}-description`}>{description}</p></div><IconButton aria-label="Stäng" onClick={onClose} disabled={busy}><CloseIcon /></IconButton></DialogTitle>
    <DialogContent>{children}</DialogContent>{error && <Alert severity="error" className="BatchSaveError">{error}</Alert>}<DialogActions>{actions}</DialogActions>
  </Dialog>;
}

function PlanningSection({ title, children }: { title: string; children: React.ReactNode }) {
  return <fieldset className="BatchFormSection PlanningFormSection"><legend>{title}</legend>{children}</fieldset>;
}

function DashBoardWeeklyPlanning() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [parents, setParents] = useState<FreshParent[] | null>(null);
  const [packages, setPackages] = useState<FreshPackage[] | null>(null);
  const [weeks, setWeeks] = useState<PlannedWeek[]>([]);
  const [plannedOrders, setPlannedOrders] = useState<PlannedOrder[]>([]);
  const [selectedWeekKey, setSelectedWeekKey] = useState<string | null>(null);
  const [draftQuantities, setDraftQuantities] = useState<Record<string, number>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const [weekDialogOpen, setWeekDialogOpen] = useState(false);
  const [weekDate, setWeekDate] = useState("");
  const [templateWeekKey, setTemplateWeekKey] = useState<string>("");
  const [savingWeek, setSavingWeek] = useState(false);
  const [labelPdfLoading, setLabelPdfLoading] = useState(false);
  const [labelDateDialogOpen, setLabelDateDialogOpen] = useState(false);
  const [labelDateAll, setLabelDateAll] = useState("");
  const [labelItems, setLabelItems] = useState<WeeklyLabelItem[]>([]);
  const [labelOrderDialogOpen, setLabelOrderDialogOpen] = useState(false);
  const [orderedLabelItems, setOrderedLabelItems] = useState<WeeklyLabelItem[]>([]);
  const [draggedLabelIndex, setDraggedLabelIndex] = useState<number | null>(null);
  const [labelIncludeItems, setLabelIncludeItems] = useState<Record<string, boolean>>({});
  const [labelIncludeBoxes, setLabelIncludeBoxes] = useState<Record<string, boolean>>({});
  const [planPdfLoading, setPlanPdfLoading] = useState(false);
  const [planPdfDialogOpen, setPlanPdfDialogOpen] = useState(false);
  const [planPdfWeek, setPlanPdfWeek] = useState<PlannedWeek | null>(null);
  const [planExceptions, setPlanExceptions] = useState<Record<string, number>>({});
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [onlyOrdered, setOnlyOrdered] = useState(false);
  const [matrixExpanded, setMatrixExpanded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [plansLoaded, setPlansLoaded] = useState({ weeks: false, orders: false });
  const [weekError, setWeekError] = useState("");
  const [exportError, setExportError] = useState("");
  const [labelWeek, setLabelWeek] = useState<PlannedWeek | null>(null);
  const [deletingWeek, setDeletingWeek] = useState<PlannedWeek | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const inFlightKeys = useRef(new Set<string>());
  const pendingWrites = useRef<Record<string, { customer: Customer; packageItem: FreshPackage; value: number; week: PlannedWeek }>>({});
  const [copyRetry, setCopyRetry] = useState<{ week: PlannedWeek; rows: PlannedOrder[] } | null>(null);
  const hasPending = Object.keys(draftQuantities).length > 0;


  useEffect(() => () => {
    Object.values(timers.current).forEach((timer) => clearTimeout(timer));
  }, []);

  useEffect(() => {
    return subscribeDashboardCustomers((items) => setCustomers(items),
      () => setLoadError("Kunderna kunde inte hämtas."));
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, "products_fresh"), orderBy("name_sv")), (snapshot) => {
      setParents(snapshot.docs.map((item) => ({ id: item.id, name_sv: item.data().name_sv || "", name_en: item.data().name_en || "", icon: item.data().icon || "", url: item.data().url || "", link: item.data().link || item.id, subtitle_sv: item.data().subtitle_sv || "", mustard: item.data().mustard === true })));
    }, () => setLoadError("Produkterna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, "products_fresh_package"), orderBy("createdAt", "desc")), (snapshot) => {
      setPackages(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as FreshPackage)));
    }, () => setLoadError("Förpackningarna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, PLANNED_WEEK_COLLECTION), orderBy("weekStart", "asc")), (snapshot) => {
      const nextWeeks = snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as PlannedWeek));
      setWeeks(nextWeeks);
      setPlansLoaded((state) => ({ ...state, weeks: true }));
      setSelectedWeekKey((current) => nextWeeks.some((week) => week.weekKey === current) ? current : nextWeeks.find((week) => week.weekKey >= currentWeekKey())?.weekKey || nextWeeks[nextWeeks.length - 1]?.weekKey || null);
    }, () => setLoadError("Planeringsveckorna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(firebaseStore, PLANNED_ORDER_COLLECTION), orderBy("createdAt", "desc")), (snapshot) => {
      setPlannedOrders(snapshot.docs.map((item) => ({ firebaseId: item.id, ...item.data() } as PlannedOrder)));
      setPlansLoaded((state) => ({ ...state, orders: true }));
    }, () => setLoadError("Beställningarna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => {
    if (user === null && loading === false) navigate("/login");
  }, [user, loading, navigate]);

  const activeCustomers = useMemo(() => (customers || []).filter((customer) => customer.active !== false), [customers]);
  const bagPackages = useMemo(() => (packages || [])
    .filter((packageItem) => !packageItem.isBox && packageItem.active !== false)
    .sort((a, b) => {
      const productA = parents?.find((parent) => parent.id === a.parentId)?.name_sv || a.parentId;
      const productB = parents?.find((parent) => parent.id === b.parentId)?.name_sv || b.parentId;
      return `${productA} ${packageLabel(a)}`.localeCompare(`${productB} ${packageLabel(b)}`, "sv-SE", { numeric: true });
    }), [packages, parents]);

  const selectedWeek = weeks.find((week) => week.weekKey === selectedWeekKey);
  const weekOptions = useMemo(() => {
    const options = new Map(weeks.map((week) => [week.weekKey, week]));
    const start = moment().startOf("isoWeek");
    for (let offset = 1; offset <= 12; offset += 1) {
      const future = start.clone().add(offset, "weeks");
      const weekKey = future.format("GGGG-[W]WW");
      if (!options.has(weekKey)) options.set(weekKey, { firebaseId: "", weekKey, weekStart: future.valueOf(), createdAt: 0 });
    }
    return Array.from(options.values()).sort((a, b) => a.weekStart - b.weekStart);
  }, [weeks]);
  const weekIncomplete = copyRetry?.week.weekKey === selectedWeekKey;
  const selectedOrders = plannedOrders.filter((order) => order.weekKey === selectedWeekKey);
  const isReady = customers !== null && parents !== null && packages !== null && plansLoaded.weeks && plansLoaded.orders;

  const getProductName = (productId: string): string => parents?.find((parent) => parent.id === productId)?.name_sv || productId;
  const getQuantityKey = (customerId: string, packageId: string): string => `${selectedWeekKey}:${customerId}:${packageId}`;
  const getPersistedQuantity = (customerId: string, packageId: string): number => selectedOrders
    .filter((order) => order.customerId === customerId && order.packageId === packageId)
    .reduce((sum, order) => sum + order.quantity, 0);

  const isPackageActive = (packageItem: FreshPackage): boolean => {
    if (packageItem.active === false) return false;
    if (!packageItem.boundBagPackageId) return true;
    return (packages || []).find((candidate) => candidate.id === packageItem.boundBagPackageId)?.active !== false;
  };

  const preparePlannedWeekLabels = (week: PlannedWeek): WeeklyLabelItem[] => {
    const grouped = new Map<string, WeeklyLabelItem>();
    plannedOrders.filter((order) => order.weekKey === week.weekKey).forEach((order) => {
      const packageItem = (packages || []).find((item) => item.id === order.packageId);
      const product = packageItem ? parents?.find((item) => item.id === packageItem.parentId) : undefined;
      if (!packageItem || !product || packageItem.isBox || !isPackageActive(packageItem)) return;
      const existing = grouped.get(packageItem.id);
      if (existing) existing.amount += order.quantity;
      else grouped.set(packageItem.id, { product, packageItem, amount: order.quantity, harvestDate: null });
    });

    // Keep each bag label next to its paired box label in the generated PDF.
    return Array.from(grouped.values()).flatMap((bagItem) => {
      const boxItems = (packages || [])
        .filter((item) => item.isBox && item.boundBagPackageId === bagItem.packageItem.id && isPackageActive(item))
        .map((boxPackage) => ({
          product: bagItem.product,
          packageItem: boxPackage,
          amount: Math.ceil(bagItem.amount / Math.max(1, getBoxMultiplier(boxPackage, bagItem.packageItem))),
          harvestDate: null,
        }));
      return [bagItem, ...boxItems];
    });
  };

  const isLabelIncluded = (item: WeeklyLabelItem) => labelIncludeItems[item.packageItem.id] !== false && (!item.packageItem.isBox || (labelIncludeBoxes[item.packageItem.boundBagPackageId || ""] === true && labelIncludeItems[item.packageItem.boundBagPackageId || ""] !== false));
  const exportPlannedWeekLabels = async () => {
    const selectedItems = orderedLabelItems.filter(isLabelIncluded);
    if (!selectedItems.length || selectedItems.some((item) => !item.harvestDate)) { setExportError("Välj etiketter och ange skördedatum för alla valda produkter."); return; }
    setExportError("");
    setLabelPdfLoading(true);
    try {
      const itemsToPrint = selectedItems;
      const blob = await createWeeklyOrderLabelsPdf(itemsToPrint);
      saveAs(blob, `VERTfarm--etiketter--planerad-vecka-${moment(labelWeek?.weekStart).isoWeek()}.pdf`);
      setLabelOrderDialogOpen(false);
    } catch (error) {
      console.error("Error exporting planned week labels:", error);
      setExportError("Etiketterna kunde inte exporteras. Försök igen.");
    } finally {
      setLabelPdfLoading(false);
    }
  };

  const openPlannedWeekLabelDates = (week: PlannedWeek) => {
    setExportError(""); setLabelWeek(week);
    const items = preparePlannedWeekLabels(week);
    if (items.length === 0) {
      setExportError("Veckan innehåller inga aktiva påsbeställningar att skriva ut.");
      return;
    }
    const includeItems: Record<string, boolean> = {};
    items.forEach((item) => { includeItems[item.packageItem.id] = true; });
    setLabelIncludeItems(includeItems);
    const includeBoxes: Record<string, boolean> = {};
    items.forEach((item) => {
      if (!item.packageItem.isBox) includeBoxes[item.packageItem.id] = false;
    });
    setLabelIncludeBoxes(includeBoxes);
    setLabelItems(items);
    setLabelDateAll("");
    setLabelDateDialogOpen(true);
  };

  const continueToLabelOrder = () => {
    const included = labelItems.filter(isLabelIncluded);
    if (!included.length || included.some((item) => !item.harvestDate)) { setExportError("Välj etiketter och ange skördedatum för alla valda produkter."); return; }
    setExportError("");
    setOrderedLabelItems(included);
    setLabelDateDialogOpen(false);
    setLabelOrderDialogOpen(true);
  };

  const moveLabelItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= orderedLabelItems.length || fromIndex === toIndex) return;
    setOrderedLabelItems((items) => {
      const next = [...items];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const openPlannedWeekPdf = (week: PlannedWeek) => {
    setExportError("");
    setPlanPdfWeek(week);
    setPlanExceptions({});
    setPlanPdfDialogOpen(true);
  };

  const exportPlannedWeekPdf = async () => {
    if (!planPdfWeek) return;
    setExportError("");
    setPlanPdfLoading(true);
    try {
      const weekOrders = plannedOrders.filter((order) => order.weekKey === planPdfWeek.weekKey);
      const columns: PlannedPdfColumn[] = bagPackages
        .filter((packageItem) => isPackageActive(packageItem))
        .map((packageItem) => ({
          packageId: packageItem.id,
          productName: getProductName(packageItem.parentId),
          packageName: packageLabel(packageItem),
        }));
      const document = (
        <PlannedWeekPdf
          weekLabel={`Vecka ${moment(planPdfWeek.weekStart).isoWeek()} · ${moment(planPdfWeek.weekStart).format("YYYY-MM-DD")}`}
          columns={columns}
          customers={activeCustomers}
          orders={weekOrders}
          exceptions={planExceptions}
        />
      );
      const blob = await pdf(document).toBlob();
      saveAs(blob, `VERTfarm--planerad-vecka-${moment(planPdfWeek.weekStart).isoWeek()}.pdf`);
      setPlanPdfDialogOpen(false);
    } catch (error) {
      console.error("Error exporting planned week PDF:", error);
      setExportError("PDF-filen kunde inte skapas. Försök igen.");
    } finally {
      setPlanPdfLoading(false);
    }
  };

  const applyLabelDateToAll = (dateValue: string) => {
    setLabelDateAll(dateValue);
    const timestamp = dateValue ? moment(dateValue).startOf("day").valueOf() : null;
    setLabelItems((items) => items.map((item) => ({ ...item, harvestDate: timestamp })));
  };

  const setLabelItemDate = (index: number, dateValue: string) => {
    const timestamp = dateValue ? moment(dateValue).startOf("day").valueOf() : null;
    setLabelItems((items) => {
      const selectedItem = items[index];
      if (!selectedItem || selectedItem.packageItem.isBox) return items;
      return items.map((item, itemIndex) => itemIndex === index || item.packageItem.boundBagPackageId === selectedItem.packageItem.id
        ? { ...item, harvestDate: timestamp }
        : item);
    });
  };

  const saveQuantity = async (customer: Customer, packageItem: FreshPackage, value: number, week: PlannedWeek) => {
    const orderId = plannedOrderId(week.weekKey, customer.id, packageItem.id);
    const orderRef = doc(firebaseStore, PLANNED_ORDER_COLLECTION, orderId);
    if (value <= 0) {
      await deleteDoc(orderRef);
      return;
    }
    await setDoc(orderRef, {
      weekKey: week.weekKey,
      weekStart: week.weekStart,
      customerId: customer.id,
      customerName: customer.name,
      productId: packageItem.parentId,
      productName: getProductName(packageItem.parentId),
      packageId: packageItem.id,
      packageLabel: packageLabel(packageItem),
      quantity: value,
      createdAt: plannedOrders.find((order) => order.weekKey === week.weekKey && order.customerId === customer.id && order.packageId === packageItem.id)?.createdAt || Date.now(),
      updatedAt: Date.now(),
    });
  };

  const persistQuantity = async (key: string) => {
    const pending = pendingWrites.current[key];
    if (!pending || inFlightKeys.current.has(key)) return;
    if (timers.current[key]) { clearTimeout(timers.current[key]); delete timers.current[key]; }
    inFlightKeys.current.add(key);
    setSavingKeys((state) => ({ ...state, [key]: true }));
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
    try {
      await saveQuantity(pending.customer, pending.packageItem, pending.value, pending.week);
      delete pendingWrites.current[key];
      setDraftQuantities((state) => { const next = { ...state }; delete next[key]; return next; });
    } catch (error) {
      console.error("Error saving planned quantity:", error);
      setSaveErrors((state) => ({ ...state, [key]: `${weekLabel(pending.week)} · ${pending.customer.name} · ${getProductName(pending.packageItem.parentId)} ${packageLabel(pending.packageItem)}` }));
    } finally {
      inFlightKeys.current.delete(key);
      setSavingKeys((state) => { const next = { ...state }; delete next[key]; return next; });
    }
  };

  const scheduleQuantity = (customer: Customer, packageItem: FreshPackage, value: number) => {
    if (!selectedWeek || !Number.isInteger(value) || value < 0 || deleteLoading || weekIncomplete) return;
    const key = getQuantityKey(customer.id, packageItem.id);
    if (inFlightKeys.current.has(key)) return;
    setDraftQuantities((state) => ({ ...state, [key]: value }));
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
    pendingWrites.current[key] = { customer, packageItem, value, week: selectedWeek };
    if (timers.current[key]) clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => { void persistQuantity(key); }, 700);
  };

  const restoreQuantity = (key: string) => {
    if (inFlightKeys.current.has(key)) return;
    if (timers.current[key]) { clearTimeout(timers.current[key]); delete timers.current[key]; }
    delete pendingWrites.current[key];
    setDraftQuantities((state) => { const next = { ...state }; delete next[key]; return next; });
    setSaveErrors((state) => { const next = { ...state }; delete next[key]; return next; });
  };

  const addWeek = async () => {
    if ((!weekDate && !copyRetry) || hasPending || savingWeek) return;
    const start = copyRetry ? moment(copyRetry.week.weekStart) : moment(weekDate, "YYYY-MM-DD", true).startOf("isoWeek");
    if (!start.isValid() || (!copyRetry && !start.isAfter(moment().startOf("isoWeek")))) { setWeekError("Välj en vecka framåt i tiden."); return; }
    const weekKey = start.format("GGGG-[W]WW");
    if (!copyRetry && weeks.some((week) => week.weekKey === weekKey)) { setSelectedWeekKey(weekKey); setWeekDialogOpen(false); return; }
    setSavingWeek(true); setWeekError("");
    try {
      let week = copyRetry?.week;
      if (!week) {
        const data = { weekKey, weekStart: start.valueOf(), createdAt: Date.now() };
        const created = await addDoc(collection(firebaseStore, PLANNED_WEEK_COLLECTION), data);
        week = { ...data, firebaseId: created.id };
        const createdWeek = week;
        setWeeks((current) => current.some((item) => item.weekKey === createdWeek.weekKey) ? current :
          [...current, createdWeek].sort((a, b) => a.weekStart - b.weekStart));
      }
      const rows = copyRetry?.rows || plannedOrders.filter((order) => order.weekKey === templateWeekKey && order.quantity > 0);
      const results = await Promise.allSettled(rows.map((order) => setDoc(doc(firebaseStore, PLANNED_ORDER_COLLECTION, plannedOrderId(week!.weekKey, order.customerId, order.packageId)), {
        weekKey: week!.weekKey, weekStart: week!.weekStart, customerId: order.customerId, customerName: order.customerName,
        productId: order.productId, productName: order.productName, packageId: order.packageId, packageLabel: order.packageLabel,
        quantity: order.quantity, createdAt: Date.now(), updatedAt: Date.now(),
      })));
      const failedRows = rows.filter((_, index) => results[index].status === "rejected");
      setSelectedWeekKey(week.weekKey);
      if (failedRows.length) { setCopyRetry({ week, rows: failedRows }); setWeekError(`Veckan skapades, men ${failedRows.length} rader återstår att kopiera. Försök igen för att slutföra kopieringen.`); return; }
      setCopyRetry(null); setWeekDialogOpen(false); setWeekDate(""); setTemplateWeekKey("");
    } catch (error) { console.error("Error adding planned week:", error); setWeekError("Veckan kunde inte skapas. Dina val finns kvar, försök igen."); }
    finally { setSavingWeek(false); }
  };

  const deleteWeek = async () => {
    if (!deletingWeek || hasPending) return;
    setDeleteLoading(true); setDeleteError("");
    try {
      const weekOrders = plannedOrders.filter((order) => order.weekKey === deletingWeek.weekKey);
      const results = await Promise.allSettled(weekOrders.map((order) => deleteDoc(doc(firebaseStore, PLANNED_ORDER_COLLECTION, order.firebaseId))));
      if (results.some((result) => result.status === "rejected")) throw new Error("Alla rader kunde inte tas bort");
      await deleteDoc(doc(firebaseStore, PLANNED_WEEK_COLLECTION, deletingWeek.firebaseId));
      if (selectedWeekKey === deletingWeek.weekKey) setSelectedWeekKey(weeks.find((item) => item.weekKey !== deletingWeek.weekKey)?.weekKey || null);
      setDeleteOpen(false);
    } catch (error) { console.error("Error deleting planned week:", error); setDeleteError("Borttagningen kunde inte slutföras. Försök igen för att ta bort återstående rader och veckan."); }
    finally { setDeleteLoading(false); }
  };

  const getAmount = (customerId: string, packageId: string) => draftQuantities[getQuantityKey(customerId, packageId)] ?? getPersistedQuantity(customerId, packageId);
  const getCustomerTotal = (customerId: string) => bagPackages.reduce((sum, pkg) => sum + getAmount(customerId, pkg.id), 0);
  const visibleCustomers = activeCustomers.filter((customer) => customer.name.toLocaleLowerCase("sv-SE").includes(customerSearch.trim().toLocaleLowerCase("sv-SE")) && (!onlyOrdered || getCustomerTotal(customer.id) > 0));
  const visiblePackages = bagPackages.filter((pkg) => `${getProductName(pkg.parentId)} ${packageLabel(pkg)}`.toLocaleLowerCase("sv-SE").includes(productSearch.trim().toLocaleLowerCase("sv-SE")));
  const totalPlanned = activeCustomers.reduce((sum, customer) => sum + getCustomerTotal(customer.id), 0);
  const totalVisible = visibleCustomers.reduce((sum, customer) => sum + visiblePackages.reduce((subtotal, pkg) => subtotal + getAmount(customer.id, pkg.id), 0), 0);
  const filtered = !!customerSearch.trim() || !!productSearch.trim() || onlyOrdered;
  const selectedIndex = weekOptions.findIndex((week) => week.weekKey === selectedWeekKey);
  const nextWeekStart = selectedWeek ? moment(selectedWeek.weekStart).add(1, "week").startOf("isoWeek") : null;
  const nextWeek = weekOptions[selectedIndex + 1] || (nextWeekStart ? {
    firebaseId: "", weekKey: nextWeekStart.format("GGGG-[W]WW"), weekStart: nextWeekStart.valueOf(), createdAt: 0,
  } : null);
  const weekToCreate = weekDate && moment(weekDate, "YYYY-MM-DD", true).isValid() ? { firebaseId: "", weekKey: moment(weekDate).format("GGGG-[W]WW"), weekStart: moment(weekDate).startOf("isoWeek").valueOf(), createdAt: 0 } : null;
  const existingWeek = weeks.find((week) => week.weekKey === weekToCreate?.weekKey);
  const validWeekDate = !!copyRetry || (!!weekToCreate && moment(weekToCreate.weekStart).isAfter(moment().startOf("isoWeek")));
  const includedLabels = labelItems.filter(isLabelIncluded);
  const labelsReady = includedLabels.length > 0 && includedLabels.every((item) => !!item.harvestDate);
  const pdfOrders = plannedOrders.filter((order) => order.weekKey === planPdfWeek?.weekKey);
  const pdfQuantity = (packageId: string) => pdfOrders.filter((order) => order.packageId === packageId && activeCustomers.some((customer) => customer.id === order.customerId)).reduce((sum, order) => sum + order.quantity, 0);
  const invalidExceptions = Object.values(planExceptions).some((value) => !Number.isInteger(value));
  const hiddenOrders = selectedOrders.filter((order) => !activeCustomers.some((customer) => customer.id === order.customerId) || !bagPackages.some((pkg) => pkg.id === order.packageId));
  const openNewWeek = (date?: string) => {
    setWeekError("");
    if (!copyRetry) {
      const next = date ? moment(date, "YYYY-MM-DD", true).startOf("isoWeek") :
        moment(Math.max(moment().startOf("isoWeek").valueOf(), selectedWeek?.weekStart || 0)).add(1, "week").startOf("isoWeek");
      if (!date) while (weeks.some((week) => week.weekKey === next.format("GGGG-[W]WW"))) next.add(1, "week");
      setWeekDate(next.format("YYYY-MM-DD")); setTemplateWeekKey("");
    }
    setWeekDialogOpen(true);
  };
  const selectPlanningWeek = (week: PlannedWeek) => {
    if (weeks.some((item) => item.weekKey === week.weekKey)) setSelectedWeekKey(week.weekKey);
    else openNewWeek(moment(week.weekStart).format("YYYY-MM-DD"));
  };
  const saveStatus = <div className={`PlanningSaveStatus${Object.keys(saveErrors).length ? " hasError" : ""}`} role="status" aria-live="polite">{Object.keys(saveErrors).length ? <><span>{Object.keys(saveErrors).length} ändringar kunde inte sparas.</span><Button size="small" color="inherit" onClick={() => Object.keys(saveErrors).forEach((key) => { void persistQuantity(key); })}>Försök igen</Button></> : hasPending ? <><CircularProgress size={14} color="inherit" /><span>{Object.keys(draftQuantities).length} ändringar sparas…</span></> : <><CheckCircleOutlineIcon fontSize="small" /><span>Alla ändringar sparade</span></>}</div>;

  const matrix = <>
    <div className="PlanningMatrixTools" role="search" aria-label="Filtrera planeringsmatrisen"><div className="PlanningSearchFields"><TextField fullWidth size="small" label="Sök kund" value={customerSearch} onChange={(event) => setCustomerSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} /><TextField fullWidth size="small" label="Sök produkt eller förpackning" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} /></div><div className="PlanningMatrixFilterRow"><FormControlLabel control={<Checkbox size="small" checked={onlyOrdered} onChange={(event) => setOnlyOrdered(event.target.checked)} />} label="Bara kunder med beställningar" />{filtered && <Button color="inherit" size="small" onClick={() => { setCustomerSearch(""); setProductSearch(""); setOnlyOrdered(false); }}>Rensa filter</Button>}<span>{visibleCustomers.length} kunder · {visiblePackages.length} förpackningar</span></div></div>
    {visibleCustomers.length && visiblePackages.length ? <div className="PlanningTableScroll" role="region" aria-label="Planeringsmatris, rulla i sidled för fler förpackningar" tabIndex={0}><table className="PlanningTable"><caption className="PlanningVisuallyHidden">{selectedWeek ? weekLabel(selectedWeek) : "Veckobeställningar"}. Planerade paket per kund och förpackning.</caption><thead><tr><th scope="col" className="PlanningCustomerColumn">Kund</th>{visiblePackages.map((pkg) => <th scope="col" key={pkg.id}><strong>{getProductName(pkg.parentId)}</strong><span>{packageLabel(pkg)}</span></th>)}<th scope="col" className="PlanningTotalColumn">Summa<small>paket</small></th></tr></thead><tbody>{visibleCustomers.map((customer) => <tr key={customer.firebaseId}><th scope="row" className="PlanningCustomerColumn">{customer.name}</th>{visiblePackages.map((pkg) => {
      const key = getQuantityKey(customer.id, pkg.id), amount = getAmount(customer.id, pkg.id);
      const label = `${customer.name}, ${getProductName(pkg.parentId)}, ${packageLabel(pkg)}`;
      const pending = draftQuantities[key] !== undefined;
      return <td key={pkg.id} className={`PlanningQuantityCell${amount ? " hasValue" : ""}${pending ? " isPending" : ""}${saveErrors[key] ? " hasError" : ""}`}><div className="PlanningQuantityControl"><button type="button" aria-label={`Minska ${label}`} disabled={amount === 0 || savingKeys[key] || deleteLoading || weekIncomplete} onClick={() => scheduleQuantity(customer, pkg, Math.max(0, amount - 1))}>−</button><input aria-label={`Antal paket: ${label}`} type="number" min="0" step="1" value={amount || ""} placeholder="0" disabled={savingKeys[key] || deleteLoading || weekIncomplete} aria-invalid={!!saveErrors[key]} onChange={(event) => scheduleQuantity(customer, pkg, event.target.value ? Number(event.target.value) : 0)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void persistQuantity(key); } if (event.key === "Escape") { event.preventDefault(); restoreQuantity(key); } }} /><button type="button" aria-label={`Öka ${label}`} disabled={savingKeys[key] || deleteLoading || weekIncomplete} onClick={() => scheduleQuantity(customer, pkg, amount + 1)}>+</button></div>{saveErrors[key] ? <div className="PlanningCellFeedback"><span>Inte sparat</span><button type="button" aria-label={`Försök spara ${label} igen`} onClick={() => { void persistQuantity(key); }}>Försök igen</button><button type="button" aria-label={`Återställ ${label}`} onClick={() => restoreQuantity(key)}>Återställ</button></div> : pending && <span className="PlanningCellPending">{savingKeys[key] ? "Sparar…" : "Väntar på att sparas"}</span>}</td>;
    })}<td className="PlanningRowTotal">{visiblePackages.reduce((sum, pkg) => sum + getAmount(customer.id, pkg.id), 0).toLocaleString("sv-SE")}</td></tr>)}</tbody><tfoot><tr><th scope="row" className="PlanningCustomerColumn">Summa{filtered && <small>synligt urval</small>}</th>{visiblePackages.map((pkg) => <td key={pkg.id}>{visibleCustomers.reduce((sum, customer) => sum + getAmount(customer.id, pkg.id), 0).toLocaleString("sv-SE")}</td>)}<td className="PlanningRowTotal">{totalVisible.toLocaleString("sv-SE")}</td></tr></tfoot></table></div> : <div className="BatchEmpty"><CalendarMonthOutlinedIcon /><strong>{!activeCustomers.length ? "Inga aktiva kunder" : !bagPackages.length ? "Inga aktiva påsförpackningar" : "Inget matchar dina filter"}</strong><p>{filtered ? "Prova en annan sökning eller rensa filtren." : "Lägg till kunder och påsförpackningar för att fylla i beställningar."}</p></div>}
    <div className="PlanningMatrixFooter"><p>Antal paket · Sparas automatiskt efter en kort paus. Enter sparar direkt, Esc återställer en osparad ändring.</p>{saveStatus}</div>
  </>;

  if (!isReady || loadError) return <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace PlanningWorkspace"><h2 className="FreshTabTitle">Veckobeställningar</h2>{loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => { setLoadError(""); setReload((value) => value + 1); }}>Försök igen</Button>}>{loadError}</Alert> : <div className="BatchEmpty" role="status"><CircularProgress color="inherit" size={24} /><p>Hämtar veckor, kunder och beställningar…</p></div>}</div>;

  return <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace PlanningWorkspace">
    <div className="FreshTabToolbar"><div><p className="FreshEyebrow">Planering</p><h2 className="FreshTabTitle">Veckobeställningar</h2><p className="FreshTabSummary">Planera kundernas mängder, sammanställ veckan och förbered etiketter.</p></div><div className="PlanningPrimaryActions"><IconButton title="Om Veckobeställningar" aria-label="Om Veckobeställningar" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton><Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={() => openNewWeek()} disabled={hasPending || deleteLoading || !!copyRetry}>Ny vecka</Button></div></div>
    {weeks.length ? <section className="PlanningWeekBar" aria-label="Välj planeringsvecka">
      <div className="PlanningWeekPicker">
        <IconButton aria-label="Föregående planeringsvecka" disabled={selectedIndex <= 0 || deleteLoading || (!weekOptions[selectedIndex - 1]?.firebaseId && (hasPending || !!copyRetry))} onClick={() => selectPlanningWeek(weekOptions[selectedIndex - 1])}><ChevronLeftIcon /></IconButton>
        <TextField select fullWidth size="small" label="Planeringsvecka" value={selectedWeekKey || ""} onChange={(event) => {
          const week = weekOptions.find((item) => item.weekKey === event.target.value);
          if (week) selectPlanningWeek(week);
        }} disabled={deleteLoading}>
          {weekOptions.map((week) => <MenuItem value={week.weekKey} key={week.weekKey} disabled={!week.firebaseId && (hasPending || !!copyRetry)}>
            {weekLabel(week)} · {moment(week.weekStart).format("D/M")}{!week.firebaseId ? " · ny planering" : week.weekKey === currentWeekKey() ? " · aktuell vecka" : week.weekStart > moment().startOf("isoWeek").valueOf() ? " · framtida vecka" : ""}
          </MenuItem>)}
        </TextField>
        <IconButton aria-label="Nästa planeringsvecka" title={nextWeek?.firebaseId ? "Nästa planeringsvecka" : "Skapa nästa planeringsvecka"} disabled={!nextWeek || deleteLoading || (!nextWeek.firebaseId && (hasPending || !!copyRetry))} onClick={() => nextWeek && selectPlanningWeek(nextWeek)}><ChevronRightIcon /></IconButton>
      </div>
      <div className="PlanningWeekActions"><Button color="inherit" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => selectedWeek && openPlannedWeekPdf(selectedWeek)} disabled={!selectedWeek || hasPending || weekIncomplete || planPdfLoading || !bagPackages.length}>Vecko-PDF</Button><Button color="inherit" variant="outlined" startIcon={<LocalPrintshopOutlinedIcon />} onClick={() => selectedWeek && openPlannedWeekLabelDates(selectedWeek)} disabled={!selectedWeek || hasPending || weekIncomplete || labelPdfLoading || !selectedOrders.some((order) => order.quantity > 0)}>Etiketter</Button><IconButton aria-label="Ta bort vald vecka" title="Ta bort vecka" disabled={!selectedWeek || hasPending || weekIncomplete || deleteLoading} onClick={() => { setDeletingWeek(selectedWeek || null); setDeleteError(""); setDeleteOpen(true); }}><DeleteIcon fontSize="small" /></IconButton></div>
    </section> : <div className="BatchEmpty PlanningEmptyWeek"><CalendarMonthOutlinedIcon /><strong>Skapa din första planeringsvecka</strong><p>Börja med en tom vecka eller kopiera mängder från en sparad planering.</p><Button color="inherit" variant="outlined" startIcon={<AddIcon />} onClick={() => openNewWeek()}>Ny vecka</Button></div>}
    {exportError && !planPdfDialogOpen && !labelDateDialogOpen && !labelOrderDialogOpen && <Alert severity="error" className="PlanningPageAlert" onClose={() => setExportError("")}>{exportError}</Alert>}
    {copyRetry && !weekDialogOpen && <Alert className="PlanningPageAlert" severity="warning" action={<Button color="inherit" onClick={() => setWeekDialogOpen(true)}>Slutför kopiering</Button>}>{copyRetry.rows.length} mallrader återstår att kopiera till {weekLabel(copyRetry.week).toLowerCase()}. Slutför kopieringen för att redigera eller exportera veckan.</Alert>}
    {selectedWeek && <>
      {!!Object.keys(saveErrors).length && <Alert className="PlanningPageAlert" severity="error">Kunde inte spara: {Object.values(saveErrors).join("; ")}. Försök igen vid matrisen.</Alert>}
      <section className="PlanningOverview" aria-label="Översikt för vald vecka"><div><span>Planerad mängd</span><strong>{totalPlanned.toLocaleString("sv-SE")} <small>paket</small></strong></div><div><span>Kunder med beställning</span><strong>{activeCustomers.filter((customer) => getCustomerTotal(customer.id) > 0).length} <small>av {activeCustomers.length}</small></strong></div><div><span>Förpackningar beställda</span><strong>{bagPackages.filter((pkg) => activeCustomers.some((customer) => getAmount(customer.id, pkg.id) > 0)).length} <small>av {bagPackages.length}</small></strong></div></section>
      {!!hiddenOrders.length && <Alert className="PlanningPageAlert" severity="info">{hiddenOrders.length} sparade rader hör till kunder eller förpackningar som inte längre är aktiva och visas inte i matrisen.</Alert>}
      {!matrixExpanded && <section className="PlanningBoard" aria-labelledby="planning-board-title"><div className="PlanningBoardHeader"><div><p className="FreshEyebrow">Beställningsmatris</p><h3 id="planning-board-title">{weekLabel(selectedWeek)}</h3><p>{weekRange(selectedWeek)} · Planerade mängder, utan lagerreservationer.</p></div><Button color="inherit" size="small" startIcon={<OpenInFullIcon />} onClick={() => setMatrixExpanded(true)}>Större matris</Button></div>{matrix}</section>}
      {!matrixExpanded && <p className="PlanningWorkflowHint">När veckan ska genomföras: öppna Veckofördelning och välj Importera vecka. PDF och etiketter omfattar hela den valda veckan, oavsett matrisens filter.</p>}
    </>}
    <Dialog className="PlanningMatrixDialog PlanningWorkspace" fullScreen open={matrixExpanded} onClose={() => setMatrixExpanded(false)} aria-labelledby="planning-expanded-title"><div className="PlanningBoardHeader"><div><p className="FreshEyebrow">Beställningsmatris</p><h2 id="planning-expanded-title">{selectedWeek ? weekLabel(selectedWeek) : "Veckobeställningar"}</h2></div><Button color="inherit" startIcon={<CloseIcon />} onClick={() => setMatrixExpanded(false)}>Stäng större matris</Button></div>{matrixExpanded && matrix}</Dialog>

    <PlanningDialog id="planning-new-week" open={weekDialogOpen} onClose={() => setWeekDialogOpen(false)} title={copyRetry ? "Slutför kopiering" : "Ny planeringsvecka"} description="Välj en framtida vecka och börja tomt eller kopiera en tidigare planering." busy={savingWeek} error={weekError} actions={<><Button color="inherit" disabled={savingWeek} onClick={() => setWeekDialogOpen(false)}>{copyRetry ? "Stäng" : "Avbryt"}</Button><Button color="inherit" variant="contained" disabled={savingWeek || hasPending || !validWeekDate} onClick={addWeek}>{savingWeek ? "Skapar planering…" : copyRetry ? "Försök kopiera igen" : existingWeek ? "Öppna befintlig vecka" : "Skapa vecka"}</Button></>}>
      <div className="PlanningForm"><PlanningSection title="Vecka"><TextField fullWidth label="Datum i veckan" type="date" size="small" value={weekDate} onChange={(event) => setWeekDate(event.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: moment().add(1, "week").startOf("isoWeek").format("YYYY-MM-DD") }} disabled={savingWeek || !!copyRetry} error={!!weekDate && !validWeekDate} helperText={weekDate && !validWeekDate ? "Välj ett datum efter den aktuella veckan." : "Valfritt datum väljer hela veckan, måndag till söndag."} />{weekToCreate && <div className="PlanningPreview"><CalendarMonthOutlinedIcon /><div><strong>{weekLabel(weekToCreate)}</strong><span>{weekRange(weekToCreate)}</span></div></div>}</PlanningSection><PlanningSection title="Utgångspunkt"><TextField select size="small" fullWidth label="Kopiera från vecka" value={templateWeekKey} disabled={savingWeek || !!copyRetry || !!existingWeek} onChange={(event) => setTemplateWeekKey(event.target.value)}><MenuItem value="">Tom planering</MenuItem>{weeks.map((week) => <MenuItem key={week.weekKey} value={week.weekKey}>{weekLabel(week)}</MenuItem>)}</TextField><p className="PlanningHint">{existingWeek && !copyRetry ? "Veckan finns redan. Öppna den befintliga planeringen för att fortsätta." : templateWeekKey ? `${plannedOrders.filter((order) => order.weekKey === templateWeekKey && order.quantity > 0).length} beställningsrader kopieras. Originalveckan behålls.` : "Mängderna börjar på noll för alla aktiva kunder och påsförpackningar."}</p></PlanningSection></div>
    </PlanningDialog>

    <PlanningDialog id="planning-delete" open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Ta bort planeringsveckan?" description="Veckan och dess planerade beställningar tas bort permanent." busy={deleteLoading} error={deleteError} actions={<><Button color="inherit" disabled={deleteLoading} onClick={() => setDeleteOpen(false)}>Avbryt</Button><Button className="PlanningDeleteButton" variant="contained" color="inherit" disabled={deleteLoading || hasPending} onClick={deleteWeek}>{deleteLoading ? "Tar bort…" : "Ta bort vecka"}</Button></>}><div className="PlanningPreview"><CalendarMonthOutlinedIcon /><div><strong>{deletingWeek ? weekLabel(deletingWeek) : ""}</strong><span>{deletingWeek ? weekRange(deletingWeek) : ""}</span></div></div><p className="PlanningHint">{plannedOrders.filter((order) => order.weekKey === deletingWeek?.weekKey).length} beställningsrader · {plannedOrders.filter((order) => order.weekKey === deletingWeek?.weekKey).reduce((sum, order) => sum + order.quantity, 0)} planerade paket.</p></PlanningDialog>

    <PlanningDialog id="planning-pdf" open={planPdfDialogOpen} onClose={() => setPlanPdfDialogOpen(false)} wide title="Exportera vecko-PDF" description={`${planPdfWeek ? weekLabel(planPdfWeek) : ""} · Kundmatris med mängder och en separat avvikelserad.`} busy={planPdfLoading} error={exportError} actions={<><Button color="inherit" disabled={planPdfLoading} onClick={() => setPlanPdfDialogOpen(false)}>Avbryt</Button><Button color="inherit" variant="contained" startIcon={<PictureAsPdfIcon />} onClick={exportPlannedWeekPdf} disabled={planPdfLoading || invalidExceptions}>{planPdfLoading ? "Skapar PDF…" : "Exportera PDF"}</Button></>}>
      <div className="PlanningForm"><p className="PlanningHint">Justeringar gäller bara denna PDF. Ange ett positivt eller negativt heltal; lämna tomt för ingen avvikelse.</p><div className="PlanningExportList">{bagPackages.filter(isPackageActive).map((pkg) => <div className="PlanningExportRow" key={pkg.id}><div><strong>{getProductName(pkg.parentId)}</strong><span>{packageLabel(pkg)} · {pdfQuantity(pkg.id)} beställda paket</span></div><TextField size="small" type="number" label="Avvikelse" value={planExceptions[pkg.id] ?? ""} onChange={(event) => setPlanExceptions((state) => ({ ...state, [pkg.id]: event.target.value ? Number(event.target.value) : 0 }))} inputProps={{ step: 1, "aria-label": `Avvikelse: ${getProductName(pkg.parentId)} ${packageLabel(pkg)}` }} error={planExceptions[pkg.id] !== undefined && !Number.isInteger(planExceptions[pkg.id])} helperText={planExceptions[pkg.id] !== undefined && !Number.isInteger(planExceptions[pkg.id]) ? "Ange ett heltal." : undefined} disabled={planPdfLoading} /><span className="PlanningExportTotal">{pdfQuantity(pkg.id) + (planExceptions[pkg.id] || 0)}<small>i PDF</small></span></div>)}</div><div className="PlanningExportSummary"><span>Totalt i PDF</span><strong>{bagPackages.filter(isPackageActive).reduce((sum, pkg) => sum + pdfQuantity(pkg.id) + (planExceptions[pkg.id] || 0), 0)} paket</strong></div></div>
    </PlanningDialog>

    <PlanningDialog id="planning-label-dates" open={labelDateDialogOpen} onClose={() => setLabelDateDialogOpen(false)} wide title="Förbered etiketter" description={`${labelWeek ? weekLabel(labelWeek) : ""} · Steg 1 av 2: välj etiketter och skördedatum.`} busy={labelPdfLoading} error={exportError} actions={<><Button color="inherit" disabled={labelPdfLoading} onClick={() => setLabelDateDialogOpen(false)}>Avbryt</Button><Button color="inherit" variant="contained" onClick={continueToLabelOrder} disabled={labelPdfLoading || !labelsReady}>Fortsätt till ordning</Button></>}>
      <div className="PlanningForm"><PlanningSection title="Gemensamt skördedatum"><p className="PlanningHint">Ange datumet som ska tryckas. Du kan sedan ändra det per produkt nedan.</p><TextField fullWidth size="small" type="date" label="Skördedatum för alla" value={labelDateAll} onChange={(event) => applyLabelDateToAll(event.target.value)} InputLabelProps={{ shrink: true }} disabled={labelPdfLoading} /></PlanningSection><div className="PlanningLabelList">{labelItems.filter((item) => !item.packageItem.isBox).map((item) => { const included = labelIncludeItems[item.packageItem.id] !== false; const boxes = labelItems.filter((box) => box.packageItem.isBox && box.packageItem.boundBagPackageId === item.packageItem.id); return <div className={`PlanningLabelCard${included ? "" : " isExcluded"}`} key={item.packageItem.id}><div className="PlanningLabelCardHeading"><FormControlLabel control={<Checkbox checked={included} onChange={(event) => setLabelIncludeItems((state) => ({ ...state, [item.packageItem.id]: event.target.checked }))} disabled={labelPdfLoading} />} label={`${item.product.name_sv} · ${packageLabel(item.packageItem)}`} /><span>{item.amount} påsetiketter</span></div><div className="PlanningLabelCardBody"><div>{boxes.length > 0 ? <FormControlLabel control={<Checkbox size="small" checked={included && labelIncludeBoxes[item.packageItem.id] === true} onChange={(event) => setLabelIncludeBoxes((state) => ({ ...state, [item.packageItem.id]: event.target.checked }))} disabled={labelPdfLoading || !included} />} label={`Ta med ${boxes.reduce((sum, box) => sum + box.amount, 0)} boxetiketter`} /> : <p className="PlanningHint">Ingen kopplad boxförpackning.</p>}<p className="PlanningHint">{included ? "Pås- och boxetiketter får samma datum." : "Produkten tas inte med i utskriften."}</p></div><TextField size="small" type="date" label="Skördedatum" inputProps={{ "aria-label": `Skördedatum: ${item.product.name_sv} ${packageLabel(item.packageItem)}` }} value={item.harvestDate ? moment(item.harvestDate).format("YYYY-MM-DD") : ""} onChange={(event) => setLabelItemDate(labelItems.indexOf(item), event.target.value)} InputLabelProps={{ shrink: true }} disabled={labelPdfLoading || !included} helperText={included && !item.harvestDate ? "Välj ett datum för att fortsätta." : undefined} /></div></div>; })}</div><div className="PlanningExportSummary"><span>{includedLabels.length} etikettyper valda</span><strong>{includedLabels.reduce((sum, item) => sum + item.amount, 0)} etiketter</strong></div></div>
    </PlanningDialog>

    <PlanningDialog id="planning-label-order" open={labelOrderDialogOpen} onClose={() => setLabelOrderDialogOpen(false)} wide title="Välj utskriftsordning" description={`${labelWeek ? weekLabel(labelWeek) : ""} · Steg 2 av 2: kontrollera och sortera etiketter.`} busy={labelPdfLoading} error={exportError} actions={<><Button color="inherit" disabled={labelPdfLoading} onClick={() => { setExportError(""); setLabelOrderDialogOpen(false); setLabelDateDialogOpen(true); }}>Tillbaka</Button><Button color="inherit" variant="contained" startIcon={<LocalPrintshopOutlinedIcon />} onClick={exportPlannedWeekLabels} disabled={labelPdfLoading || !orderedLabelItems.length}>{labelPdfLoading ? "Skapar etiketter…" : "Skapa etikett-PDF"}</Button></>}>
      <div className="PlanningForm"><p className="PlanningHint">Använd pilarna eller dra raderna till önskad ordning. Etiketterna skrivs ut uppifrån och ned.</p><ol className="PlanningLabelOrderList">{orderedLabelItems.map((item, index) => <li className={`PlanningLabelOrderRow${draggedLabelIndex === index ? " isDragging" : ""}`} key={item.packageItem.id} draggable={!labelPdfLoading} onDragStart={() => setDraggedLabelIndex(index)} onDragOver={(event) => event.preventDefault()} onDrop={() => { if (draggedLabelIndex !== null && !labelPdfLoading) moveLabelItem(draggedLabelIndex, index); setDraggedLabelIndex(null); }} onDragEnd={() => setDraggedLabelIndex(null)}><DragIndicatorIcon className="PlanningDragHandle" /><span className="PlanningOrderNumber">{index + 1}</span><div><strong>{item.product.name_sv} · {packageLabel(item.packageItem)}</strong><span>{item.packageItem.isBox ? "Box" : "Påse"} · {item.amount} etiketter · {moment(item.harvestDate).format("YYYY-MM-DD")}</span></div><div className="PlanningReorderActions"><IconButton size="small" aria-label={`Flytta ${item.product.name_sv} ${packageLabel(item.packageItem)} upp`} disabled={labelPdfLoading || index === 0} onClick={() => moveLabelItem(index, index - 1)}><ArrowUpwardIcon fontSize="small" /></IconButton><IconButton size="small" aria-label={`Flytta ${item.product.name_sv} ${packageLabel(item.packageItem)} ned`} disabled={labelPdfLoading || index === orderedLabelItems.length - 1} onClick={() => moveLabelItem(index, index + 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></div></li>)}</ol><div className="PlanningExportSummary" role="status"><span>{orderedLabelItems.length} etikettyper</span><strong>{orderedLabelItems.reduce((sum, item) => sum + item.amount, 0)} etiketter</strong></div></div>
    </PlanningDialog>

    <PlanningDialog id="planning-info" open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} title="Om Veckobeställningar" description="Planera kommande veckors beställningar och förbered utskrifter." actions={<Button color="inherit" variant="contained" onClick={() => setInfoDialogOpen(false)}>Stäng</Button>}><div className="PlanningForm"><PlanningSection title="1. Välj eller skapa vecka"><p className="PlanningHint">Välj en sparad vecka eller någon av de kommande tolv veckorna i listan. Nästa-pilen kan skapa en ny framtida vecka. Med Ny vecka kan du välja valfritt framtida datum och börja tomt eller kopiera en tidigare planering.</p></PlanningSection><PlanningSection title="2. Fyll i matrisen"><p className="PlanningHint">Kunder visas som rader och aktiva påsförpackningar som kolumner. Använd minus, plus eller skriv antal direkt. Rubriker och kundnamn följer med när du rullar.</p><p className="PlanningHint">Ändringar sparas efter en kort paus. Sparstatus och eventuella fel visas vid matrisen. Enter sparar direkt, Esc återställer en osparad ändring. Vänta tills allt är sparat innan du lämnar fliken.</p><p className="PlanningHint">Sök eller visa enbart kunder med beställningar. Matrisens summor gäller det synliga urvalet; översikten ovanför gäller alla aktiva kunder och påsförpackningar.</p></PlanningSection><PlanningSection title="3. Exportera och genomför"><p className="PlanningHint">Vecko-PDF kan kompletteras med avvikelser som bara påverkar utskriften. Etiketter förbereds i två steg: välj produkter och skördedatum, sedan ordning. Exporterna gäller hela den valda veckan.</p><p className="PlanningHint">Planeringen reserverar inget lager. När veckan ska genomföras importerar du den i Veckofördelning, där mängderna kontrolleras mot lager och FIFO-regeln.</p></PlanningSection></div></PlanningDialog>
  </div>;
}

export default DashBoardWeeklyPlanning;
